import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Container,
  Fade,
  useTheme,
  useMediaQuery,
  styled
} from "@mui/material";
import { KeyboardArrowDown, Movie, Theaters } from "@mui/icons-material";

// Styled components using MUI's styled API
const StyledButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: 25,
  padding: '12px 24px',
  backgroundColor: variant === 'creator' 
    ? 'rgba(255, 140, 0, 0.2)' // Subtle orange for creator button
    : 'rgba(255, 255, 255, 0.15)', // Slightly stronger white for audience button
  backdropFilter: 'blur(10px)',
  color: '#fff',
  transition: 'all 0.3s ease',
  textTransform: 'none',
  marginRight: theme.spacing(2),
  border: variant === 'creator'
    ? '1px solid rgba(255, 165, 0, 0.3)' // Subtle orange border
    : '1px solid rgba(255, 255, 255, 0.3)', // Stronger white border for audience
  '&:hover': {
    backgroundColor: variant === 'creator'
      ? 'rgba(255, 140, 0, 0.3)' // Stronger orange on hover
      : 'rgba(255, 255, 255, 0.25)', // Stronger white on hover
    transform: 'scale(1.05)',
    boxShadow: variant === 'creator'
      ? '0 4px 12px rgba(255, 140, 0, 0.15)' // Subtle orange glow
      : '0 4px 12px rgba(255, 255, 255, 0.2)', // Stronger white glow
  },
  [theme.breakpoints.down('sm')]: {
    marginRight: 0,
    marginBottom: theme.spacing(2),
  }
}));

const ScrollIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(8),
  left: '50%',
  transform: 'translateX(-50%)',
  cursor: 'pointer',
  animation: 'bounce 2s infinite',
  '@keyframes bounce': {
    '0%, 100%': {
      transform: 'translateX(-50%) translateY(0)',
    },
    '50%': {
      transform: 'translateX(-50%) translateY(10px)',
    },
  },
}));

const FloatingElement = styled(Box)(({ delay = 0 }) => ({
  position: 'absolute',
  width: '150px',
  height: '150px',
  background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
  animation: 'float 15s infinite',
  animationDelay: `${delay}s`,
  '@keyframes float': {
    '0%, 100%': {
      transform: 'translateY(0) rotate(0deg)',
    },
    '50%': {
      transform: 'translateY(-100px) rotate(180deg)',
    },
  },
}));

const Hero = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isVisible, setIsVisible] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToFeatures = () => {
    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleShareStories = () => {
    window.location.href = 'https://creator.kalakaar.io/signup';
  };

  const watchFestivals = () => {
    window.location.href = 'https://watch.kalakaar.io/';
  };

  return (
    <Box
      sx={{
        minHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        position: 'relative',
        background: 'linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.8))',
        overflow: 'hidden',
        mt: '64px',
      }}
    >
      {/* Animated background elements */}
      <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
        {[...Array(5)].map((_, i) => (
          <FloatingElement
            key={i}
            delay={i * 1.5}
            sx={{
              left: `${i * 25}%`,
              top: `${Math.random() * 100}%`,
            }}
          />
        ))}
      </Box>

      <Container maxWidth="lg">
        <Fade in={isVisible} timeout={1000}>
          <Grid container spacing={4} justifyContent="center" alignItems="center">
            <Grid item xs={12} sx={{ textAlign: 'center' }}>
                              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '4rem' },
                  fontWeight: 700,
                  color: 'white',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                  mb: 2,
                }}
              >
                Showcase Your Creative Work,
                <br />
                Reach New Audiences
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
                  color: 'rgba(255,255,255,0.9)',
                  mb: 6,
                }}
              >
                A creator-first platform designed specifically for
                {!isMobile && <br />}
                independent filmmakers and artists
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'center',
                  gap: 2,
                  '& > *': {
                    width: { xs: '100%', sm: 'auto' },
                    minWidth: { xs: '200px', sm: '180px', md: '220px' }
                  }
                }}
              >
                <StyledButton
                  variant="contained"
                  onClick={handleShareStories}
                  startIcon={<Movie />}
                  variant="creator" // Added variant for creator styling
                  sx={{
                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.2rem' },
                    padding: { xs: '8px 16px', sm: '10px 20px', md: '12px 24px' },
                  }}
                >
                  Get Started
                </StyledButton>

                <StyledButton
                  variant="contained"
                  onClick={watchFestivals}
                  startIcon={<Theaters />}
                  sx={{
                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.2rem' },
                    padding: { xs: '8px 16px', sm: '10px 20px', md: '12px 24px' },
                  }}
                >
                  Browse Films
                </StyledButton>
              </Box>
            </Grid>
          </Grid>
        </Fade>
      </Container>

      <ScrollIndicator onClick={scrollToFeatures}>
        <KeyboardArrowDown sx={{ color: 'white', fontSize: 40 }} />
      </ScrollIndicator>
    </Box>
  );
};

export default Hero;